/**
 * Allocation Log Context
 * 
 * Tracks every lead allocation attempt (success or rejection)
 * for debugging, analytics, and admin dashboard.
 * 
 * Uses database for persistence.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import {
  useRoutingLogs,
  useCreateRoutingLog,
  useDeleteAllRoutingLogs,
  type CreateRoutingLogInput,
} from "@/hooks/useRoutingLogsDatabase";

// --- Type Definitions ---

export interface AllocationBreakdown {
  bidScore: number;
  qualityScore: number;
  bidWeight: number;
  qualityWeight: number;
  finalScore: number;
}

export interface EvaluatedAgent {
  agentId: string;
  agentName: string;
  eligible: boolean;
  effectiveBid?: number;
  priorityScore?: number;
  breakdown?: AllocationBreakdown;
  rejectionCode?: string;
  rejectionReason?: string;
}

export interface AgentPickAttempt {
  agentId: string;
  agentName: string;
  probability: number;
  pickOrder: number;
  outcome: 'selected' | 'rejected' | 'skipped';
  rejectionCode?: string;
  rejectionReason?: string;
}

// Pool Selection Metadata
export interface AgentPacingScore {
  agentId: string;
  agentName: string;
  pacingScore: number;
  remainingLeads: number;
  daysRemaining: number;
  deliveryProgress: number;
  isBehindTarget: boolean;
  // Goal-aware pacing fields
  expectedDeliveredByNow?: number;
  paceDeficit?: number;
  paceDeficitScore?: number;
  // Delivery pressure fields
  guaranteedMinimum?: number;
  remainingGuaranteedLeads?: number;
  deliveryPressureScore?: number;
}

export interface GoalAwarePacingMeta {
  agentsBehindPace: number;
  agentsAheadOfPace: number;
  averagePaceDeficit: number;
  dynamicPoolExpansion: boolean;
  poolSelectionReason: string;
}

export interface PoolMeta {
  strategy: string;
  poolSize: number;
  bucketId: string;
  bucketIndex: number;
  totalBuckets: number;
  pacingBoostApplied: boolean;
  pacingScores: AgentPacingScore[];
  preFilteredCount: number;
  postFilteredCount: number;
  selectionTimestamp: number;
  poolAgentIds?: string[];
  // Goal-aware pacing fields
  goalAwarePacing?: GoalAwarePacingMeta;
  // Delivery pressure enforcement fields
  deliveryPressure?: {
    agentsWithGuarantee: number;
    agentsUnderPressure: number;
    averagePressureScore: number;
    pressureBoostApplied: boolean;
    totalGuaranteedDemand: number;
    demandExceedsSupplyWarning: boolean;
  };
  // Contract pressure enforcement fields
  contractPressure?: {
    agentsWithContracts: number;
    agentsUnderPressure: number;
    totalCommitted: number;
    totalDelivered: number;
    averageUrgencyScore: number;
    atRiskContracts: number;
    contractPressureApplied: boolean;
  };
  // Execution guardrails applied
  executionGuardrails?: {
    guardrailsApplied: boolean;
    agentsWithPoolBoost: number;
    totalBoostApplied: number;
  };
}

// Guardrail summary for routing logs
export interface GuardrailSummary {
  guardrailsApplied: boolean;
  pacingCap?: number;
  retryMultiplier?: number;
  poolBoost?: number;
  guaranteedOverride?: number;
  guardrailReasons: string[];
}

export interface AllocationLog {
  id: string;
  leadId: string;
  leadTypeId: string;
  leadTypeName: string;
  leadState: string;
  result: "success" | "rejected" | "unsold";
  rejectionReason?: string;
  rejectionCode?: string;
  chargedAmount?: number;
  priorityScore?: number;
  allocationBreakdown?: AllocationBreakdown;
  timestamp: string;
  // Multi-agent routing fields
  selectedAgentId?: string;
  selectedAgentName?: string;
  evaluatedAgents?: EvaluatedAgent[];
  pickAttempts?: AgentPickAttempt[];
  totalAgentsEvaluated?: number;
  eligibleCount?: number;
  ineligibleCount?: number;
  // Lead lifecycle fields
  leadStatusBefore?: string;
  leadStatusAfter?: string;
  retryCount?: number;
  // Pool selection fields
  poolMeta?: PoolMeta;
  // Execution guardrails
  guardrailSummary?: GuardrailSummary;
}

interface AllocationLogState {
  logs: AllocationLog[];
  isLoading: boolean;
}

interface AllocationLogActions {
  /** Log a successful allocation (legacy single-agent) */
  logSuccess: (data: {
    leadId: string;
    leadTypeId: string;
    leadTypeName: string;
    leadState: string;
    chargedAmount: number;
    priorityScore: number;
    allocationBreakdown?: AllocationBreakdown;
  }) => void;

  /** Log a rejected allocation (legacy single-agent) */
  logRejection: (data: {
    leadId: string;
    leadTypeId: string;
    leadTypeName: string;
    leadState: string;
    reason: string;
    code: string;
  }) => void;

  /** Log a multi-agent routing result with full transparency */
  logMultiAgentResult: (data: {
    leadId: string;
    leadTypeId: string;
    leadTypeName: string;
    leadState: string;
    result: "success" | "unsold";
    selectedAgentId?: string;
    selectedAgentName?: string;
    chargedAmount?: number;
    priorityScore?: number;
    allocationBreakdown?: AllocationBreakdown;
    rejectionReason?: string;
    rejectionCode?: string;
    evaluatedAgents: EvaluatedAgent[];
    pickAttempts: AgentPickAttempt[];
    totalAgentsEvaluated: number;
    eligibleCount: number;
    ineligibleCount: number;
    // Lead lifecycle fields
    leadStatusBefore?: string;
    leadStatusAfter?: string;
    retryCount?: number;
    // Pool selection fields
    poolMeta?: PoolMeta;
  }) => void;

  /** Get all logs (read-only) */
  getAllocationLogs: () => AllocationLog[];

  /** Get logs filtered by result */
  getLogsByResult: (result: "success" | "rejected" | "unsold") => AllocationLog[];

  /** Clear all logs (for testing) */
  clearLogs: () => void;
}

// --- Context ---

const AllocationLogContext = createContext<{
  state: AllocationLogState;
  actions: AllocationLogActions;
} | undefined>(undefined);

// --- Provider ---

export const AllocationLogProvider = ({ children }: { children: ReactNode }) => {
  // Database queries
  const { data: logs = [], isLoading } = useRoutingLogs();
  
  // Database mutations
  const createLogMutation = useCreateRoutingLog();
  const deleteAllMutation = useDeleteAllRoutingLogs();

  const logSuccess = useCallback((data: {
    leadId: string;
    leadTypeId: string;
    leadTypeName: string;
    leadState: string;
    chargedAmount: number;
    priorityScore: number;
    allocationBreakdown?: AllocationBreakdown;
  }) => {
    const input: CreateRoutingLogInput = {
      leadId: data.leadId,
      leadTypeId: data.leadTypeId,
      leadTypeName: data.leadTypeName,
      leadState: data.leadState,
      result: "success",
      chargedAmount: data.chargedAmount,
      priorityScore: data.priorityScore,
      allocationBreakdown: data.allocationBreakdown,
    };

    createLogMutation.mutate(input);

    // Verbose logging in dev mode
    if (import.meta.env.MODE !== "production") {
      console.log("[AllocationLog] Success:", input);
    }
  }, [createLogMutation]);

  const logRejection = useCallback((data: {
    leadId: string;
    leadTypeId: string;
    leadTypeName: string;
    leadState: string;
    reason: string;
    code: string;
  }) => {
    const input: CreateRoutingLogInput = {
      leadId: data.leadId,
      leadTypeId: data.leadTypeId,
      leadTypeName: data.leadTypeName,
      leadState: data.leadState,
      result: "rejected",
      rejectionReason: data.reason,
      rejectionCode: data.code,
    };

    createLogMutation.mutate(input);

    // Verbose logging in dev mode
    if (import.meta.env.MODE !== "production") {
      console.log("[AllocationLog] Rejected:", input);
    }
  }, [createLogMutation]);

  const logMultiAgentResult = useCallback((data: {
    leadId: string;
    leadTypeId: string;
    leadTypeName: string;
    leadState: string;
    result: "success" | "unsold";
    selectedAgentId?: string;
    selectedAgentName?: string;
    chargedAmount?: number;
    priorityScore?: number;
    allocationBreakdown?: AllocationBreakdown;
    rejectionReason?: string;
    rejectionCode?: string;
    evaluatedAgents: EvaluatedAgent[];
    pickAttempts: AgentPickAttempt[];
    totalAgentsEvaluated: number;
    eligibleCount: number;
    ineligibleCount: number;
    // Lead lifecycle fields
    leadStatusBefore?: string;
    leadStatusAfter?: string;
    retryCount?: number;
    // Pool selection fields
    poolMeta?: PoolMeta;
  }) => {
    const input: CreateRoutingLogInput = {
      leadId: data.leadId,
      leadTypeId: data.leadTypeId,
      leadTypeName: data.leadTypeName,
      leadState: data.leadState,
      result: data.result,
      selectedAgentId: data.selectedAgentId,
      selectedAgentName: data.selectedAgentName,
      chargedAmount: data.chargedAmount,
      priorityScore: data.priorityScore,
      allocationBreakdown: data.allocationBreakdown,
      rejectionReason: data.rejectionReason,
      rejectionCode: data.rejectionCode,
      evaluatedAgents: data.evaluatedAgents,
      pickAttempts: data.pickAttempts,
      totalAgentsEvaluated: data.totalAgentsEvaluated,
      eligibleCount: data.eligibleCount,
      ineligibleCount: data.ineligibleCount,
      leadStatusBefore: data.leadStatusBefore,
      leadStatusAfter: data.leadStatusAfter,
      retryCount: data.retryCount,
      poolMeta: data.poolMeta,
    };

    createLogMutation.mutate(input);

    // Verbose logging in dev mode
    if (import.meta.env.MODE !== "production") {
      console.log("[AllocationLog] Multi-Agent Result:", input);
    }
  }, [createLogMutation]);

  const getAllocationLogs = useCallback(() => {
    return [...logs];
  }, [logs]);

  const getLogsByResult = useCallback((result: "success" | "rejected" | "unsold") => {
    return logs.filter((log) => log.result === result);
  }, [logs]);

  const clearLogs = useCallback(() => {
    deleteAllMutation.mutate();
  }, [deleteAllMutation]);

  const actions: AllocationLogActions = {
    logSuccess,
    logRejection,
    logMultiAgentResult,
    getAllocationLogs,
    getLogsByResult,
    clearLogs,
  };

  return (
    <AllocationLogContext.Provider value={{ state: { logs, isLoading }, actions }}>
      {children}
    </AllocationLogContext.Provider>
  );
};

// --- Hook ---

export const useAllocationLogs = () => {
  const context = useContext(AllocationLogContext);
  if (context === undefined) {
    throw new Error("useAllocationLogs must be used within an AllocationLogProvider");
  }
  return context;
};

export default AllocationLogContext;
